import React, { useState } from 'react';
import { BookmarkIcon, ExternalLinkIcon, FileTextIcon, ShoppingCartIcon, ReceiptIcon, MinusSquareIcon, CreditCardIcon, FileSignatureIcon, AlertTriangleIcon, CheckCircleIcon, ClockIcon, XCircleIcon, PaperclipIcon, MessageSquareIcon, TagIcon, EyeIcon, ChevronUpIcon, ChevronDownIcon } from 'lucide-react';
import { StatusBadge } from '../StatusBadge';
export function DocumentGrid({
  documents,
  bookmarkedDocs,
  onToggleBookmark,
  onDocumentSelect,
  onDocumentOpen,
  isLoading,
  locale = 'en-US'
}) {
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  // Format currency based on locale
  const formatCurrency = amount => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };
  // Format date based on locale
  const formatDate = dateString => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  // Get document type icon
  const getDocumentTypeIcon = typeCode => {
    switch (typeCode) {
      case 'INV':
        return <ReceiptIcon className="h-4 w-4 text-purple-500" data-id="element-4579" />;
      case 'PO':
        return <ShoppingCartIcon className="h-4 w-4 text-blue-500" data-id="element-4580" />;
      case 'REQ':
        return <FileTextIcon className="h-4 w-4 text-green-500" data-id="element-4581" />;
      case 'CN':
        return <MinusSquareIcon className="h-4 w-4 text-red-500" data-id="element-4582" />;
      case 'EXP':
        return <CreditCardIcon className="h-4 w-4 text-yellow-500" data-id="element-4583" />;
      case 'CTR':
        return <FileSignatureIcon className="h-4 w-4 text-indigo-500" data-id="element-4584" />;
      default:
        return <FileTextIcon className="h-4 w-4 text-gray-500" data-id="element-4585" />;
    }
  };
  // Get status badge type
  const getStatusBadgeType = status => {
    switch (status) {
      case 'Approved':
      case 'Completed':
      case 'Paid':
      case 'Active':
      case 'Processed':
        return 'success';
      case 'Pending Approval':
      case 'Sent to Vendor':
        return 'info';
      case 'Draft':
        return 'default';
      case 'Exception':
      case 'Rejected':
        return 'error';
      default:
        return 'default';
    }
  };
  // Handle sort change
  const handleSort = field => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  // Sort documents based on current sort settings
  const sortedDocuments = [...documents].sort((a, b) => {
    let comparison = 0;
    switch (sortField) {
      case 'id':
        comparison = a.id.localeCompare(b.id);
        break;
      case 'type':
        comparison = a.type.localeCompare(b.type);
        break;
      case 'date':
        comparison = new Date(a.date) - new Date(b.date);
        break;
      case 'amount':
        comparison = a.amount - b.amount;
        break;
      case 'status':
        comparison = a.status.localeCompare(b.status);
        break;
      case 'vendor':
        if (a.vendor && b.vendor) {
          comparison = a.vendor.localeCompare(b.vendor);
        } else if (a.vendor) {
          comparison = -1;
        } else if (b.vendor) {
          comparison = 1;
        }
        break;
      case 'entity':
        comparison = a.entity.localeCompare(b.entity);
        break;
      case 'createdBy':
        comparison = a.createdBy.localeCompare(b.createdBy);
        break;
      default:
        comparison = 0;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });
  // Render sort indicator
  const renderSortIndicator = field => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUpIcon className="h-4 w-4 ml-1" data-id="element-4586" /> : <ChevronDownIcon className="h-4 w-4 ml-1" data-id="element-4587" />;
  };
  return <div className="overflow-x-auto" data-id="element-4588">
      {isLoading ? <div className="flex justify-center items-center py-20" data-id="element-4589">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500" data-id="element-4590"></div>
          <span className="ml-3 text-sm text-gray-500" data-id="element-4591">
            Loading documents...
          </span>
        </div> : sortedDocuments.length === 0 ? <div className="text-center py-10" data-id="element-4592">
          <p className="text-gray-500" data-id="element-4593">
            No documents found matching your criteria.
          </p>
        </div> : <table className="min-w-full divide-y divide-gray-200" data-id="element-4594">
          <thead className="bg-gray-50" data-id="element-4595">
            <tr data-id="element-4596">
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10" data-id="element-4597">
                <span className="sr-only" data-id="element-4598">Bookmark</span>
              </th>
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('id')} data-id="element-4599">
                <div className="flex items-center" data-id="element-4600">
                  ID {renderSortIndicator('id')}
                </div>
              </th>
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('type')} data-id="element-4601">
                <div className="flex items-center" data-id="element-4602">
                  Type {renderSortIndicator('type')}
                </div>
              </th>
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('date')} data-id="element-4603">
                <div className="flex items-center" data-id="element-4604">
                  Date {renderSortIndicator('date')}
                </div>
              </th>
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('amount')} data-id="element-4605">
                <div className="flex items-center" data-id="element-4606">
                  Amount {renderSortIndicator('amount')}
                </div>
              </th>
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('status')} data-id="element-4607">
                <div className="flex items-center" data-id="element-4608">
                  Status {renderSortIndicator('status')}
                </div>
              </th>
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('vendor')} data-id="element-4609">
                <div className="flex items-center" data-id="element-4610">
                  Vendor/Entity {renderSortIndicator('vendor')}
                </div>
              </th>
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('createdBy')} data-id="element-4611">
                <div className="flex items-center" data-id="element-4612">
                  Created By {renderSortIndicator('createdBy')}
                </div>
              </th>
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4613">
                Info
              </th>
              <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4614">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-4615">
            {sortedDocuments.map(document => <tr key={document.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => onDocumentSelect(document)} data-id="element-4616">
                <td className="px-3 py-4 whitespace-nowrap" onClick={e => {
            e.stopPropagation();
            onToggleBookmark(document.id);
          }} data-id="element-4617">
                  <BookmarkIcon className={`h-5 w-5 ${bookmarkedDocs.includes(document.id) ? 'text-yellow-500 fill-current' : 'text-gray-300'}`} data-id="element-4618" />
                </td>
                <td className="px-3 py-4 whitespace-nowrap" data-id="element-4619">
                  <div className="flex items-center" data-id="element-4620">
                    {getDocumentTypeIcon(document.typeCode)}
                    <span className="ml-2 text-sm font-medium text-gray-900" data-id="element-4621">
                      {document.id}
                    </span>
                  </div>
                </td>
                <td className="px-3 py-4 whitespace-nowrap" data-id="element-4622">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-4623">
                    {document.typeCode}
                  </span>
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4624">
                  {formatDate(document.date)}
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-4625">
                  {formatCurrency(document.amount)}
                </td>
                <td className="px-3 py-4 whitespace-nowrap" data-id="element-4626">
                  <StatusBadge status={getStatusBadgeType(document.status)} text={document.status} data-id="element-4627" />
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4628">
                  <div data-id="element-4629">{document.vendor || 'N/A'}</div>
                  <div className="text-xs text-gray-400" data-id="element-4630">
                    {document.entity} - {document.site}
                  </div>
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4631">
                  <div data-id="element-4632">{document.createdBy}</div>
                  <div className="text-xs text-gray-400" data-id="element-4633">
                    {formatDate(document.createdDate)}
                  </div>
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4634">
                  <div className="flex space-x-1" data-id="element-4635">
                    {document.hasAttachments && <span title={`${document.attachmentCount} attachment(s)`} data-id="element-4636">
                        <PaperclipIcon className="h-4 w-4 text-gray-400" data-id="element-4637" />
                      </span>}
                    {document.hasNotes && <span title={`${document.noteCount} note(s)`} data-id="element-4638">
                        <MessageSquareIcon className="h-4 w-4 text-gray-400" data-id="element-4639" />
                      </span>}
                    {document.hasTags && <span title={document.tags.join(', ')} data-id="element-4640">
                        <TagIcon className="h-4 w-4 text-gray-400" data-id="element-4641" />
                      </span>}
                    {document.hasWarnings && <span title="Has warnings" data-id="element-4642">
                        <AlertTriangleIcon className="h-4 w-4 text-yellow-500" data-id="element-4643" />
                      </span>}
                    {document.hasErrors && <span title="Has errors" data-id="element-4644">
                        <XCircleIcon className="h-4 w-4 text-red-500" data-id="element-4645" />
                      </span>}
                  </div>
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-4646">
                  <div className="flex justify-end space-x-2" data-id="element-4647">
                    <button onClick={e => {
                e.stopPropagation();
                onDocumentSelect(document);
              }} className="text-indigo-600 hover:text-indigo-900" data-id="element-4648">
                      <EyeIcon className="h-4 w-4" data-id="element-4649" />
                    </button>
                    <button onClick={e => {
                e.stopPropagation();
                onDocumentOpen(document);
              }} className="text-indigo-600 hover:text-indigo-900" data-id="element-4650">
                      <ExternalLinkIcon className="h-4 w-4" data-id="element-4651" />
                    </button>
                  </div>
                </td>
              </tr>)}
          </tbody>
        </table>}
    </div>;
}